import { requireEnv } from "@virtual-care/shared";
import { authenticatedFetch } from "../utils/auth";

interface FetchOptions extends RequestInit {
  body?: any;
}

class CallApiClient {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = requireEnv(process.env.VUE_APP_API_BASE_URL, "VUE_APP_API_BASE_URL");
  }

  private async request<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const url = `${this.apiBaseUrl}/api${endpoint}`;

    const config: RequestInit = {
      ...options,
    };

    if (options.body && typeof options.body === "object") {
      config.body = JSON.stringify(options.body);
    }

    const response = await authenticatedFetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async createSession(
    roomId: string,
    options?: {
      recordingEnabled?: boolean;
      callPurpose?: string;
      useGenericNurseLabel?: boolean;
      skipIncomingNotification?: boolean;
    }
  ): Promise<any> {
    const recordingEnabled = options?.recordingEnabled ?? true;
    return this.request<any>("/sessions", {
      method: "POST",
      body: {
        roomId,
        recordingEnabled,
        callPurpose: options?.callPurpose,
        useGenericNurseLabel: options?.useGenericNurseLabel,
        skipIncomingNotification: options?.skipIncomingNotification,
      },
    });
  }

  async joinSession(sessionId: string, role: "nurse" | "patient"): Promise<any> {
    return this.request<any>(`/sessions/${sessionId}/join`, {
      method: "POST",
      body: { role },
    });
  }

  async endSession(sessionId: string): Promise<any> {
    return this.request<any>(`/sessions/${sessionId}`, {
      method: "DELETE",
    });
  }

  async initiateBreakthrough(
    sessionId: string,
    reason: string
  ): Promise<{ success: boolean; sessionId: string }> {
    return this.request<{ success: boolean; sessionId: string }>(
      `/sessions/${sessionId}/breakthrough`,
      {
        method: "POST",
        body: { reason },
      }
    );
  }
}

export const callApiClient = new CallApiClient();
